import { useTranslation } from 'react-i18next';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import { createElement } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { useGenerateReportMutation } from '@/api/report/reportApiSlice';
import { ReportListItem } from './ReportListSummaryPage';

export interface ReportFilterValues {
	start?: string | null;
	end?: string | null;
	series?: string;
	hasOnline?: boolean;
}

const snackbarAction = (key: string | number) =>
	createElement(CloseIcon, { onClick: () => closeSnackbar(key) });

/**
 * Shared report download logic used by both the row direct-download
 * (filter-less reports) and the filter dialog.
 *
 * Only sends the filters the report actually declares, guards against an
 * error body that came back as a blob, and uses the server filename when
 * present.
 *
 * @returns the request succeeded and a file was downloaded
 */
export function useDownloadReport() {
	const { t } = useTranslation();
	const [generateReport, { isLoading }] = useGenerateReportMutation();

	const download = async (
		report: ReportListItem,
		values: ReportFilterValues = {}
	): Promise<boolean> => {
		const { filters } = report;

		const response = await generateReport({
			type: report.id,
			start: filters.includes('start') ? values.start ?? undefined : undefined,
			end: filters.includes('end') ? values.end ?? undefined : undefined,
			series: filters.includes('series') ? values.series || undefined : undefined,
			hasOnline: filters.includes('hasOnline') ? values.hasOnline : undefined,
		});

		// API error (non-2xx) is already surfaced by the base query error modal.
		if (!('data' in response)) return false;

		const { blob, filename } = response.data;

		// Robust guard: an error/JSON body delivered as a blob is not a file.
		if (blob.type.includes('json') || blob.type.includes('text')) {
			let message = t('reportList.downloadError');
			try {
				const text = await blob.text();
				const parsed = JSON.parse(text);
				if (parsed?.message) message = parsed.message;
			} catch {
				// keep default message
			}
			enqueueSnackbar(message, {
				variant: 'error',
				anchorOrigin: { horizontal: 'center', vertical: 'top' },
				autoHideDuration: 3000,
				action: snackbarAction,
			});
			return false;
		}

		const url = window.URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.href = url;
		link.download = filename || `${t(report.nameKey)}.xlsx`;
		document.body.appendChild(link);
		link.click();
		link.remove();
		window.URL.revokeObjectURL(url);

		enqueueSnackbar(t('reportList.downloadSuccess'), {
			variant: 'success',
			anchorOrigin: { horizontal: 'center', vertical: 'top' },
			autoHideDuration: 3000,
			action: snackbarAction,
		});
		return true;
	};

	return { download, isLoading };
}
